/**
 * Página para crear/editar roles
 * Sistema Klever Octopus - Betel Natural
 */
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { usePermissions } from '../../hooks';
import { rolesService } from '../../services';
import RoleForm from './RoleForm';
import { Loading } from '../../components/UI/Loading';

const RoleFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const { permissions: apiPermissions, loading: permissionsLoading } = usePermissions();
  const [role, setRole] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Asegurar que existan permisos mínimos para todos los módulos
  const ensureMinimumPermissions = (apiPerms) => {
    const modules = ['usuarios', 'clientes', 'proveedores', 'compras', 'inventario', 'productos', 'ventas', 'preordenes', 'cotizaciones', 'facturacion'];
    const actions = ['ver', 'crear', 'editar', 'eliminar', 'own'];
    const permissionsSet = new Map();

    // Agregar permisos existentes de la API
    apiPerms.forEach(perm => {
      const key = `${perm.module}_${perm.group}`.toLowerCase();
      permissionsSet.set(key, perm);
    });

    // Crear permisos faltantes
    let idCounter = apiPerms.length;
    modules.forEach(module => {
      actions.forEach(action => {
        const key = `${module}_${action}`.toLowerCase();
        if (!permissionsSet.has(key)) {
          // Crear permiso sintético
          const permName = `${action}_${module}`;
          permissionsSet.set(key, {
            id: `synthetic_${idCounter++}`,
            name: permName,
            display_name: `${action.charAt(0).toUpperCase() + action.slice(1)} ${module.charAt(0).toUpperCase() + module.slice(1)}`,
            module: module,
            group: action,
            description: `Permiso para ${action} ${module}`,
            roles: [],
            is_synthetic: true
          });
        }
      });
    });

    return Array.from(permissionsSet.values());
  };

  const permissions = React.useMemo(() =>
    ensureMinimumPermissions(apiPermissions),
    [apiPermissions]
  );

  // Cargar rol si es modo edición
  React.useEffect(() => {
    const loadRole = async () => {
      if (!isEditMode) return;

      setLoading(true);
      setError(null);

      try {
        const response = await rolesService.getRole(id);

        if (response.success) {
          setRole(response.data);
        } else {
          setError(response.message || 'Error al cargar el rol');
        }
      } catch (err) {
        console.error('Error loading role:', err);
        setError('Error al cargar el rol');
      } finally {
        setLoading(false);
      }
    };

    loadRole();
  }, [id, isEditMode]);

  const handleSubmit = async (roleData) => {
    try {
      let result;

      if (isEditMode) {
        result = await rolesService.updateRole(id, roleData);
      } else {
        result = await rolesService.createRole(roleData);
      }

      if (result.success) {
        // Navegar de vuelta a la lista de roles
        navigate('/system/admin/roles');
      } else {
        return result;
      }
    } catch (error) {
      console.error('Error submitting role:', error);
      return {
        success: false,
        message: 'Error al guardar el rol'
      };
    }
  };

  const handleCancel = () => {
    navigate('/system/admin/roles');
  };

  if (loading || permissionsLoading) {
    return (
      <div className="page-loading">
        <Loading message={isEditMode ? "Cargando rol..." : "Cargando permisos..."} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="role-form-page">
        <div className="page-header">
          <button
            className="btn btn--ghost"
            onClick={handleCancel}
            type="button"
          >
            <FiArrowLeft /> Volver a Roles
          </button>
        </div>
        <div className="alert alert--danger">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="role-form-page">
      {/* Header */}
      <div className="page-header" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '24px 40px',
        marginBottom: '30px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button
            className="btn btn--ghost"
            onClick={handleCancel}
            type="button"
            style={{
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              padding: '10px 16px'
            }}
          >
            <FiArrowLeft /> Volver
          </button>

          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0',
              color: 'white'
            }}>
              {isEditMode ? 'Editar Rol' : 'Crear Nuevo Rol'}
            </h1>
            <p style={{
              margin: '8px 0 0 0',
              opacity: 0.95,
              fontSize: '14px',
              fontWeight: '400',
              color: 'white'
            }}>
              {isEditMode
                ? 'Modifica la información y permisos del rol'
                : 'Configura el nombre, nivel y permisos del nuevo rol'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
        <RoleForm
          role={role}
          mode={isEditMode ? 'edit' : 'create'}
          permissions={permissions}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
};

export default RoleFormPage;
